import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { scheduleRequests } from "../../../../store/remediation/remediationSlice";
import { friendlyErrorMessage, friendlySkippedReason } from "../common/ErrorCodeMap";
import DatetimePicker from "../common/DatetimePicker";
import { useRemediationSnackbar } from "../common/SnackbarHost";
import { useActionFeedback } from "../common/hooks";
import {
  dateToIso,
  formatScheduleHint,
  getDefaultScheduledDate,
  validateScheduledIso,
} from "../common/scheduleUtils";

/**
 * Re-schedule modal — used in both single and bulk modes.
 * Drives POST /remediation/requests/schedule.
 */
const RescheduleModal = ({ open, requestIds = [], onClose, onDone }) => {
  const dispatch = useDispatch();
  const { showSnackbar } = useRemediationSnackbar();
  const [scheduledAt, setScheduledAt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setScheduledAt(dateToIso(getDefaultScheduledDate()));
      setError(null);
    }
  }, [open]);

  const handleSubmit = () => {
    const validationError = validateScheduledIso(scheduledAt);
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    dispatch(scheduleRequests({ request_ids: requestIds, scheduled_at: scheduledAt }));
  };

  useActionFeedback("schedule", ({ succeeded = [], skipped = [], error: actionError }) => {
    if (!open) return;
    setSubmitting(false);
    if (actionError) {
      showSnackbar({ message: friendlyErrorMessage(actionError), severity: "error" });
      return;
    }
    if (succeeded.length && skipped.length === 0) {
      showSnackbar({
        message: `Re-scheduled ${succeeded.length} request${succeeded.length === 1 ? "" : "s"}.`,
        severity: "success",
      });
    } else if (succeeded.length && skipped.length) {
      showSnackbar({
        message: `Re-scheduled ${succeeded.length}, skipped ${skipped.length}.`,
        severity: "warning",
        title: "Partial success",
        details: skipped.map((s) => `#${s.request_id}: ${friendlySkippedReason(s)}`),
      });
    } else {
      showSnackbar({
        message: "Couldn't re-schedule.",
        severity: "error",
        details: skipped.map((s) => `#${s.request_id}: ${friendlySkippedReason(s)}`),
      });
    }
    if (typeof onDone === "function") onDone();
    onClose();
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>
        {requestIds.length > 1
          ? `Re-schedule ${requestIds.length} requests`
          : "Re-schedule request"}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2, fontSize: 13 }}>
          {requestIds.length > 1
            ? `Sets a new scheduled time for ${requestIds.length} active requests.`
            : "Pick a new time to run this remediation."}
        </Typography>

        <DatetimePicker
          label="Run at"
          value={scheduledAt}
          onChange={(iso) => {
            setScheduledAt(iso);
            setError(null);
          }}
          fullWidth
          error={Boolean(error)}
          helperText={error || formatScheduleHint()}
        />

        {requestIds.length > 1 && (
          <Alert severity="info" sx={{ mt: 1.5, fontSize: 13 }}>
            Bulk action: every selected request will be re-scheduled to the same time.
            Per-row results show up in the toast.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: "none", fontSize: 13 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{ textTransform: "none", fontSize: 13 }}
          startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {submitting ? "Saving…" : "Re-schedule"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RescheduleModal;
